import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { ArrowRight, CheckCircle2, Mail } from "lucide-react";
import { trackCTA, trackConversion } from "@/lib/tracking";
import { useHeroFormStart } from "@/lib/hero-analytics";
import { HoneypotFields } from "@/components/site/HoneypotFields";
import { evaluateBotSignals } from "@/lib/form-security";
import { supabase } from "@/integrations/supabase/client";


const ROLES = [
  "Parent / Guardian",
  "Educator / School Staff",
  "Administrator",
  "Attorney / Legal",
  "Clinician / Therapist",
  "Advocate",
  "Other",
] as const;

const ORG_TYPES = [
  "Public school district",
  "Charter school",
  "Private / Independent school",
  "Faith-based school",
  "Homeschool / Co-op",
  "Higher education",
  "Nonprofit / Ministry",
  "Not affiliated",
  "Other",
] as const;

const TIERS = ["Foundation", "Priority", "Elite", "Not sure"] as const;

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(2, { message: "Please enter your full name." })
    .max(100),
  email: z
    .string()
    .trim()
    .email({ message: "Enter a valid email address." })
    .max(255),
  role: z
    .string()
    .refine((v) => (ROLES as readonly string[]).includes(v), {
      message: "Select the role that best describes you.",
    }),
  orgType: z
    .string()
    .refine((v) => (ORG_TYPES as readonly string[]).includes(v), {
      message: "Select a school / organization type.",
    }),
  tier: z
    .string()
    .refine((v) => (TIERS as readonly string[]).includes(v), {
      message: "Select a tier.",
    }),
});

type FieldErrors = Partial<Record<keyof z.infer<typeof schema>, string>>;

export function TierLeadCaptureForm({
  defaultTier = "Not sure",
}: {
  defaultTier?: (typeof TIERS)[number];
}) {
  const heroFormStart = useHeroFormStart("tier_lead_capture");
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const startedAtRef = useRef(Date.now());
  const interactedRef = useRef(false);
  const [values, setValues] = useState({
    name: "",
    email: "",
    role: "",
    orgType: "",
    tier: defaultTier as string,
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [botNotice, setBotNotice] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<
    { name: string; email: string; tier: string; emailSent: boolean } | null
  >(null);
  const attemptRef = useRef(0);


  const tierSlug = (t: string) =>
    t.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

  // --- Funnel instrumentation -------------------------------------------
  // Order matters: field_position lets us chart drop-off step by step.
  const FIELD_ORDER = ["name", "email", "role", "orgType", "tier"] as const;
  const completedRef = useRef<Set<string>>(new Set());
  const startedRef = useRef(false);

  const funnelProps = (over?: Record<string, unknown>) => ({
    form_id: "tier_lead_capture",
    source: "advocacy_tier_matrix",
    tier: values.tier,
    tier_slug: tierSlug(values.tier),
    role: values.role || "unset",
    org_type: values.orgType || "unset",
    ...over,
  });

  const markInteracted = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackCTA("tier_lead_capture_start", funnelProps({ default_tier: defaultTier }));
    }
    interactedRef.current = true;
  };

  const setField = <K extends keyof typeof values>(k: K, v: string) => {
    markInteracted();
    setValues((prev) => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));

    // First time a field receives a non-empty value = that step completed.
    const trimmed = v.trim();
    if (trimmed && !completedRef.current.has(k)) {
      completedRef.current.add(k);
      const position = FIELD_ORDER.indexOf(k as (typeof FIELD_ORDER)[number]) + 1;
      trackCTA("tier_lead_capture_field_complete", {
        ...funnelProps({ [k === "orgType" ? "org_type" : k]: trimmed }),
        field: k,
        field_position: position,
        fields_completed: completedRef.current.size,
        fields_total: FIELD_ORDER.length,
        completion_ratio: Number(
          (completedRef.current.size / FIELD_ORDER.length).toFixed(2),
        ),
        ms_since_start: Date.now() - startedAtRef.current,
      });
    }
  };


  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    attemptRef.current += 1;
    const attempt = attemptRef.current;
    const tier = values.tier;
    const baseProps = {
      tier,
      tier_slug: tierSlug(tier),
      attempt,
      source: "advocacy_tier_matrix",
      form_id: "tier_lead_capture",
    } as const;

    trackCTA("tier_lead_capture_submit_attempt", {
      ...baseProps,
      role: values.role,
      org_type: values.orgType,
    });

    // --- Spam gate: honeypot + interaction + min-fill-time -----------------
    const signals = evaluateBotSignals({
      formData: new FormData(formRef.current ?? (e.target as HTMLFormElement)),
      startedAt: startedAtRef.current,
      interacted: interactedRef.current,
    });
    if (signals.verdict === "drop") {
      trackCTA("tier_lead_capture_bot_blocked", {
        ...baseProps,
        verdict: "drop",
        reason: signals.reason,
      });
      // Silent no-op: never tell a bot why it failed.
      return;
    }
    if (signals.verdict === "challenge") {
      trackCTA("tier_lead_capture_bot_blocked", {
        ...baseProps,
        verdict: "challenge",
        reason: signals.reason,
      });
      setBotNotice(signals.message);
      return;
    }
    setBotNotice(null);


    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FieldErrors;
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);

      const errorFields = Object.keys(next) as (keyof FieldErrors)[];

      // One event per invalid field so each error is measurable on its own.
      errorFields.forEach((field, index) => {
        trackCTA("tier_lead_capture_field_error", {
          ...baseProps,
          role: values.role || "unset",
          org_type: values.orgType || "unset",
          field,
          field_position: FIELD_ORDER.indexOf(
            field as (typeof FIELD_ORDER)[number],
          ) + 1,
          error_order: index + 1,
          error_message: next[field],
          is_empty: !values[field],
          fields_completed: completedRef.current.size,
        });

      });

      // Aggregate rollup for the whole failed attempt.
      trackCTA("tier_lead_capture_validation_error", {
        ...baseProps,
        role: values.role,
        org_type: values.orgType,
        error_fields: errorFields.join(","),
        error_count: errorFields.length,
      });
      return;
    }
    setSubmitting(true);
    const successProps = {
      ...baseProps,
      tier: parsed.data.tier,
      tier_slug: tierSlug(parsed.data.tier),
      role: parsed.data.role,
      org_type: parsed.data.orgType,
      fields_completed: completedRef.current.size,
      fields_total: FIELD_ORDER.length,
      ms_to_submit: Date.now() - startedAtRef.current,
    };


    void (async () => {
      const { data: inserted, error } = await supabase
        .from("inquiry_submissions")
        .insert({
          lane: "advocacy",
          full_name: parsed.data.name,
          email: parsed.data.email,
          organization: parsed.data.orgType,
          role: parsed.data.role,
          event_or_focus: `FSAP retainer — ${parsed.data.tier} tier`,
          budget_range: parsed.data.tier,
          message: `Tier inquiry from the advocacy tier matrix. Best-fit tier: ${parsed.data.tier}. Role: ${parsed.data.role}. Setting: ${parsed.data.orgType}.`,
          source: "tier_lead_capture",
        })
        .select("id")
        .maybeSingle();

      if (error) {
        setSubmitting(false);
        trackCTA("tier_lead_capture_submit_error", {
          ...successProps,
          error_message: error.message,
        });
        setBotNotice(
          "We couldn't submit that just now. Please try again, or continue on the inquiry page.",
        );
        return;
      }

      // Acknowledgement email — best effort; the confirmation screen still
      // shows if email infrastructure isn't live yet.
      let emailSent = false;
      try {
        const { error: mailError } = await supabase.functions.invoke(
          "send-transactional-email",
          {
            body: {
              templateName: "tier-inquiry-acknowledgement",
              recipientEmail: parsed.data.email,
              idempotencyKey: `tier-inquiry-ack-${inserted?.id ?? parsed.data.email}`,
              templateData: {
                name: parsed.data.name,
                tier: parsed.data.tier,
                role: parsed.data.role,
                orgType: parsed.data.orgType,
              },
            },
          },
        );
        emailSent = !mailError;
      } catch {
        emailSent = false;
      }

      trackCTA("tier_lead_capture_submit", {
        ...successProps,
        destination: "confirmation",
        email_sent: emailSent,
      });
      // Tier-scoped event name so each tier can be charted independently.
      trackCTA(`tier_lead_capture_submit_${tierSlug(parsed.data.tier)}`, {
        ...successProps,
        destination: "confirmation",
        email_sent: emailSent,
      });
      trackConversion("inquiry_submit", {
        lane: "advocacy",
        source: "tier_lead_capture",
        tier: parsed.data.tier,
        tier_slug: tierSlug(parsed.data.tier),
        role: parsed.data.role,
        org_type: parsed.data.orgType,
        attempt,
      });

      setSubmitting(false);
      setBotNotice(null);
      setConfirmed({
        name: parsed.data.name,
        email: parsed.data.email,
        tier: parsed.data.tier,
        emailSent,
      });
    })();
  };



  const inputBase =
    "w-full bg-navy/70 border px-4 py-3 text-white text-sm placeholder:text-silver/50 focus:outline-none focus:border-gold transition-colors";
  const err = (k: keyof FieldErrors) =>
    errors[k] ? "border-red-500/70" : "border-border";

  if (confirmed) {
    return (
      <section
        role="status"
        aria-live="polite"
        aria-label="Inquiry received"
        className="relative mt-10 border border-gold/40 bg-navy/60 p-6 md:p-10"
      >
        <div className="max-w-2xl">
          <CheckCircle2 className="h-8 w-8 text-gold" aria-hidden="true" />
          <span className="eyebrow text-gold block mt-5">Inquiry Received</span>
          <h3 className="font-display font-bold uppercase text-white text-xl md:text-2xl mt-3 leading-tight">
            Thank you, {confirmed.name.split(" ")[0]} — your {confirmed.tier} tier
            request is in.
          </h3>
          <p className="text-silver text-sm mt-4 leading-relaxed">
            Our team reviews advocacy retainer inquiries within one business day.
            You&apos;ll receive next steps, availability, and a short intake link
            at <span className="text-white">{confirmed.email}</span>.
          </p>

          <div className="mt-6 flex items-start gap-3 border border-border bg-navy/70 p-4">
            <Mail className="h-4 w-4 text-gold mt-0.5 shrink-0" aria-hidden="true" />
            <p className="text-silver/90 text-xs leading-relaxed">
              {confirmed.emailSent
                ? `A confirmation email is on its way to ${confirmed.email}. If you don't see it in a few minutes, check your spam folder.`
                : "Your request is logged and our team has it. If a confirmation email doesn't arrive, reply to any message from us and we'll pick it up directly."}
            </p>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
            <button
              type="button"
              onClick={() => {
                trackCTA("tier_lead_capture_confirmation_cta", {
                  tier: confirmed.tier,
                  tier_slug: tierSlug(confirmed.tier),
                  destination: "/advocacy/pricing",
                });
                navigate("/advocacy/pricing");
              }}
              className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 shadow-cta hover:brightness-110 transition-all"
            >
              Review retainer tiers
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirmed(null);
                setValues({
                  name: "",
                  email: "",
                  role: "",
                  orgType: "",
                  tier: defaultTier as string,
                });
                startedAtRef.current = Date.now();
                interactedRef.current = false;
                completedRef.current = new Set();
                startedRef.current = false;

              }}
              className="text-silver hover:text-white text-xs font-display uppercase tracking-[0.2em] underline underline-offset-4"
            >
              Submit another inquiry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (

    <form
      ref={formRef}
      onSubmit={onSubmit}
      onFocusCapture={() => { markInteracted(); heroFormStart(); }}
      onPointerDownCapture={markInteracted}
      onKeyDownCapture={markInteracted}
      noValidate
      aria-label="Advocacy tier lead capture"
      className="relative mt-10 border border-gold/30 bg-navy/60 p-6 md:p-10"
    >
      <HoneypotFields />
      <div className="max-w-2xl">

        <span className="eyebrow text-gold">Start Here</span>
        <h3 className="font-display font-bold uppercase text-white text-xl md:text-2xl mt-3 leading-tight">
          Tell us the basics — we&apos;ll route your inquiry.
        </h3>
        <p className="text-silver text-sm mt-3 leading-relaxed">
          Share a few details and pick the tier that looks closest. We&apos;ll
          continue on the inquiry page with your context pre-filled.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-8">
        <div>
          <label htmlFor="tlc-name" className="block text-silver text-[11px] uppercase tracking-[0.2em] mb-2">
            Full name
          </label>
          <input
            id="tlc-name"
            type="text"
            autoComplete="name"
            maxLength={100}
            value={values.name}
            onChange={(e) => setField("name", e.target.value)}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "tlc-name-err" : undefined}
            className={`${inputBase} ${err("name")}`}
            placeholder="Jane Rivera"
          />
          {errors.name && (
            <p id="tlc-name-err" className="text-red-400 text-xs mt-1.5">
              {errors.name}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="tlc-email" className="block text-silver text-[11px] uppercase tracking-[0.2em] mb-2">
            Email
          </label>
          <input
            id="tlc-email"
            type="email"
            autoComplete="email"
            maxLength={255}
            value={values.email}
            onChange={(e) => setField("email", e.target.value)}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "tlc-email-err" : undefined}
            className={`${inputBase} ${err("email")}`}
            placeholder="you@example.com"
          />
          {errors.email && (
            <p id="tlc-email-err" className="text-red-400 text-xs mt-1.5">
              {errors.email}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="tlc-role" className="block text-silver text-[11px] uppercase tracking-[0.2em] mb-2">
            Your role
          </label>
          <select
            id="tlc-role"
            value={values.role}
            onChange={(e) => setField("role", e.target.value)}
            aria-invalid={!!errors.role}
            aria-describedby={errors.role ? "tlc-role-err" : undefined}
            className={`${inputBase} ${err("role")}`}
          >
            <option value="">Select your role…</option>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          {errors.role && (
            <p id="tlc-role-err" className="text-red-400 text-xs mt-1.5">
              {errors.role}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="tlc-org" className="block text-silver text-[11px] uppercase tracking-[0.2em] mb-2">
            School / Organization type
          </label>
          <select
            id="tlc-org"
            value={values.orgType}
            onChange={(e) => setField("orgType", e.target.value)}
            aria-invalid={!!errors.orgType}
            aria-describedby={errors.orgType ? "tlc-org-err" : undefined}
            className={`${inputBase} ${err("orgType")}`}
          >
            <option value="">Select a setting…</option>
            {ORG_TYPES.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          {errors.orgType && (
            <p id="tlc-org-err" className="text-red-400 text-xs mt-1.5">
              {errors.orgType}
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <span className="block text-silver text-[11px] uppercase tracking-[0.2em] mb-2">
            Best-fit tier
          </span>
          <div
            role="radiogroup"
            aria-label="Best-fit tier"
            className="grid grid-cols-2 md:grid-cols-4 gap-2"
          >
            {TIERS.map((t) => {
              const active = values.tier === t;
              return (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => {
                    setField("tier", t);
                    trackCTA("tier_lead_capture_tier_select", {
                      tier: t,
                      source: "advocacy_tier_matrix",
                    });
                  }}
                  className={`text-xs font-display uppercase tracking-[0.2em] px-3 py-3 border transition-colors ${
                    active
                      ? "border-gold bg-gold/10 text-white"
                      : "border-border text-silver hover:border-gold/50 hover:text-white"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {botNotice && (
        <p role="alert" className="mt-6 text-amber-300 text-xs leading-relaxed">
          {botNotice}
        </p>
      )}

      <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 shadow-cta hover:brightness-110 transition-all disabled:opacity-60"
        >
          Continue to inquiry
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
        <p className="text-silver/70 text-[11px] leading-relaxed">
          We only use these details to route your inquiry. No spam. Unsubscribe anytime.
        </p>
      </div>
    </form>
  );
}
